/**
 * Almacenamiento del mapa de credenciales (claves de usuario cifradas).
 *
 * Backend-aware:
 *   - supabase: una fila `entity="config", id="pmis_local_creds"` en
 *     `pmis_entities` → las claves son COMPARTIDAS entre todos los equipos.
 *     El admin puede restablecer la clave de cualquiera y el cambio se ve
 *     en toda la organización.
 *   - dexie / otros: `localStorage` (comportamiento del demo local).
 *
 * Las claves se guardan SIEMPRE cifradas (salt + SHA-256). Ni el sistema ni
 * el admin pueden "ver" la clave; solo verificarla o restablecerla.
 */

import type { RegistroCredencial } from "./credenciales";

const STORAGE_CREDS = "pmis_local_creds";
const BACKEND = import.meta.env.VITE_BACKEND ?? "sharepoint";
const TENANT = "local-dev";

export type MapaCredenciales = Record<string, RegistroCredencial>;

// ── localStorage (demo local) ───────────────────────────────────────────

function cargarLocal(): MapaCredenciales {
  try {
    const raw = localStorage.getItem(STORAGE_CREDS);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return {};
    return parsed as MapaCredenciales;
  } catch {
    return {};
  }
}

function guardarLocal(mapa: MapaCredenciales): void {
  localStorage.setItem(STORAGE_CREDS, JSON.stringify(mapa));
}

// ── Supabase (piloto compartido) ─────────────────────────────────────────

async function cargarSupabase(): Promise<MapaCredenciales> {
  const { supabase, TABLE } = await import("../data/adapters/supabase/supabaseClient");
  const { data, error } = await supabase()
    .from(TABLE)
    .select("payload")
    .eq("entity", "config")
    .eq("tenant_id", TENANT)
    .eq("id", STORAGE_CREDS)
    .maybeSingle();
  if (error || !data) return {};
  const payload = (data as { payload?: { mapa?: MapaCredenciales } }).payload;
  return payload?.mapa ?? {};
}

async function guardarSupabase(mapa: MapaCredenciales): Promise<void> {
  const { supabase, TABLE } = await import("../data/adapters/supabase/supabaseClient");
  const now = new Date().toISOString();
  const { error } = await supabase()
    .from(TABLE)
    .upsert(
      {
        entity: "config",
        id: STORAGE_CREDS,
        tenant_id: TENANT,
        version: 1,
        payload: { mapa },
        created_at: now,
        updated_at: now,
      },
      { onConflict: "entity,id" },
    );
  if (error) throw new Error(`No se pudo guardar credenciales en Supabase: ${error.message}`);
}

// ── API unificada ────────────────────────────────────────────────────────

export async function cargarMapaCredenciales(): Promise<MapaCredenciales> {
  return BACKEND === "supabase" ? cargarSupabase() : cargarLocal();
}

export async function guardarMapaCredenciales(mapa: MapaCredenciales): Promise<void> {
  if (BACKEND === "supabase") await guardarSupabase(mapa);
  else guardarLocal(mapa);
}
